import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

import * as M from '@core/api/models';

export interface BuildingAddressFormValue {
  id: number | null;
  buildingNumber: string;
  entranceCode: string;
}

export type BuildingAddressInput =
  | ({
      buildingNumber?: string | null;
      entranceCode?: string | null;
      id?: number | null;
    })
  | null
  | undefined;

export type BuildingAddressFormGroup = FormGroup<{
  id: FormControl<number | null>;
  buildingNumber: FormControl<string>;
  entranceCode: FormControl<string>;
}>;

export interface BuildingAddressPayloadInput {
  buildingNumber?: string | null;
  entranceCode?: string | null;
}

export function normalizeBuildingAddressValue(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function toBuildingAddressFormValue(value?: BuildingAddressInput): BuildingAddressFormValue {
  return {
    id: typeof value?.id === 'number' ? value.id : null,
    buildingNumber: value?.buildingNumber ?? '',
    entranceCode: value?.entranceCode ?? '',
  };
}

export function createBuildingAddressFormGroup(
  fb: FormBuilder,
  value?: BuildingAddressInput
): BuildingAddressFormGroup {
  const normalized = toBuildingAddressFormValue(value);

  return fb.group({
    id: fb.control(normalized.id),
    buildingNumber: fb.control(normalized.buildingNumber, { nonNullable: true }),
    entranceCode: fb.control(normalized.entranceCode, { nonNullable: true }),
  });
}

export function createBuildingAddressFormArray(
  fb: FormBuilder,
  values?: BuildingAddressInput[]
): FormArray<BuildingAddressFormGroup> {
  const source = Array.isArray(values) && values.length > 0 ? values : [undefined];
  return fb.array(source.map((value) => createBuildingAddressFormGroup(fb, value)));
}

export function toBuildingAddressRequest(
  value: BuildingAddressPayloadInput
): M.BuildingAddressRequest {
  const buildingNumber = normalizeBuildingAddressValue(value.buildingNumber);
  const entranceCode = normalizeBuildingAddressValue(value.entranceCode);

  return {
    ...(buildingNumber ? { buildingNumber } : {}),
    ...(entranceCode ? { entranceCode } : {}),
  };
}

export function toBuildingAddressPayload(
  value: BuildingAddressFormValue
): { id?: number; buildingNumber?: string; entranceCode?: string } {
  const normalized = toBuildingAddressRequest(value);

  return {
    ...(value.id ? { id: value.id } : {}),
    ...normalized,
  };
}

export function areBuildingAddressValuesEqual(
  source: Pick<M.BuildingAddressDto, 'buildingNumber' | 'entranceCode'>,
  candidate: Partial<M.BuildingAddressRequest>
): boolean {
  return normalizeBuildingAddressValue(source.buildingNumber) === normalizeBuildingAddressValue(candidate.buildingNumber)
    && normalizeBuildingAddressValue(source.entranceCode) === normalizeBuildingAddressValue(candidate.entranceCode);
}

export function formatBuildingAddress(
  address: Pick<M.BuildingAddressDto, 'buildingNumber' | 'entranceCode'>
): string {
  const buildingNumber = normalizeBuildingAddressValue(address.buildingNumber);
  const entranceCode = normalizeBuildingAddressValue(address.entranceCode);

  if (buildingNumber && entranceCode) {
    return `${buildingNumber} / kl. ${entranceCode}`;
  }

  if (buildingNumber) {
    return buildingNumber;
  }

  if (entranceCode) {
    return `Klatka ${entranceCode}`;
  }

  return 'Pusty adres';
}

export function formatBuildingAddressesSummary(
  addresses: Array<Pick<M.BuildingAddressDto, 'buildingNumber' | 'entranceCode'>> | undefined
): string {
  if (!addresses?.length) {
    return 'Brak adresów';
  }

  return addresses.map(formatBuildingAddress).join(', ');
}
